
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  convertRequestErrorToMap,
  Nullable,
  useResource,
  useToast,
} from "@tager/admin-services";
import { FormFooter, TagerFormSubmitEvent, FormField } from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";

import { getTermFormUrl, getTermListUrl } from "@/utils/paths";
import {
  convertFormValuesToTermCreationPayload,
  convertFormValuesToTermUpdatePayload,
  convertTermToFormValues,
  FormValues,
} from "@/views/TermForm/TermForm.helpers";
import { TermType } from "@/typings/model";
import { createTerm, getTerm, updateTerm } from "@/services/requests";

export default defineComponent({
  name: "TermForm",
  components: { FormFooter, FormField, Page },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const termId = computed<string>(() => route.params.termId as string);

    const isCreation = computed<boolean>(() => termId.value === "create");

    const [fetchTerm, { data: term, loading: isTermLoading }] = useResource<
      Nullable<TermType>
    >({
      fetchResource: () => getTerm(termId.value),
      initialValue: null,
      resourceName: "term",
    });

    onMounted(() => {
      if (isCreation.value) return;

      fetchTerm();
    });

    const values = ref<FormValues>(convertTermToFormValues(term.value));

    const errors = ref<Record<string, string>>({});
    const isSubmitting = ref<boolean>(false);

    watch(term, () => {
      values.value = convertTermToFormValues(term.value);
    });

    function submitForm(event: TagerFormSubmitEvent) {
      isSubmitting.value = true;

      const creationBody = convertFormValuesToTermCreationPayload(values.value);

      const updateBody = convertFormValuesToTermUpdatePayload(values.value);
      const requestPromise = isCreation.value
        ? createTerm(creationBody)
        : updateTerm(termId.value, updateBody);

      requestPromise
        .then((response) => {
          errors.value = {};

          if (event.type === "create") {
            router.push(getTermFormUrl({ termId: response.data.id }));
          }
          if (event.type === "create_exit" || event.type === "save_exit") {
            router.push(getTermListUrl());
          }
          if (event.type === "create_create-another") {
            values.value = convertTermToFormValues(null);
          }

          toast.show({
            variant: "success",
            title: "Success",
            body: isCreation.value
              ? `Слово успешно добавлено в словарь`
              : "Слово успешно изменено",
          });
        })
        .catch((error) => {
          console.error(error);
          errors.value = convertRequestErrorToMap(error);
          toast.show({
            variant: "danger",
            title: "Ошибка",
            body: isCreation.value
              ? `Ошибка при добавлении слова в словарь`
              : "Ошибка при изменении слова в словаре",
          });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    }

    const isContentLoading = computed<boolean>(() => isTermLoading.value);

    const pageTitle = computed<string>(() => {
      return isCreation.value
        ? "Создание слова"
        : `Редактирование слова ${values.value.term}`;
    });

    return {
      backButtonUrl: getTermListUrl(),
      values,
      errors,
      isSubmitting,
      submitForm,
      pageTitle,
      isContentLoading,
      term,
      isCreation,
    };
  },
});
